import { AxiosResponse } from 'axios';
import httpClient from './httpClient';
import { PendingSlackInstallation, SlackConnection, SlackIdentityStatus } from '../types/Slack';

const SLACK_BASE_ROUTE = '/v1/integrations/slack';

/**
 * Read a parked pending Slack installation without spending it. Not organization-scoped:
 * the user has not chosen an organization yet. The response carries only `teamId` and
 * `teamName` - the server never sends the bot token to the browser.
 */
export async function getPendingSlackInstallation(
  pendingToken: string
): Promise<AxiosResponse<PendingSlackInstallation>> {
  return httpClient({
    method: 'GET',
    url: `${SLACK_BASE_ROUTE}/pending-installation`,
    params: { pending: pendingToken },
  });
}

/**
 * Binds the pending Slack workspace to an organization. `organizationId` is the NUMERIC
 * organization id - the server's organizationGuard reads it as a number - and the route
 * requires the caller to be ADMIN on that organization.
 */
export async function confirmSlackInstallation(input: {
  organizationId: number;
  pendingToken: string;
}): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'POST',
    url: `${SLACK_BASE_ROUTE}/${input.organizationId}/installations/confirm`,
    data: { pendingToken: input.pendingToken },
  });
}

/**
 * Resolves the slack.com URL the browser should be sent to in order to install the
 * app into a workspace. ADMIN-guarded server-side.
 */
export async function createSlackInstallUrl(organizationId: number): Promise<AxiosResponse<{ installUrl: string }>> {
  return httpClient({
    method: 'POST',
    url: `${SLACK_BASE_ROUTE}/${organizationId}/connect`,
  });
}

/** The organization's workspace connection. Carries no token material. */
export async function getSlackInstallation(organizationId: number): Promise<AxiosResponse<SlackConnection>> {
  return httpClient({
    method: 'GET',
    url: `${SLACK_BASE_ROUTE}/${organizationId}/installation`,
  });
}

/**
 * Disconnects the workspace. The server soft-deletes, so identity links and
 * delivery history stay readable and reconnecting does not force a relink.
 */
export async function disconnectSlackInstallation(organizationId: number): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'DELETE',
    url: `${SLACK_BASE_ROUTE}/${organizationId}/installation`,
  });
}

/**
 * Resolves the "Sign in with Slack" URL for linking the CALLER's own Slack account.
 * MEMBER-guarded: the acting user comes from the session, never from the request.
 */
export async function createSlackIdentityLinkUrl(
  organizationId: number
): Promise<AxiosResponse<{ authorizeUrl: string }>> {
  return httpClient({
    method: 'POST',
    url: `${SLACK_BASE_ROUTE}/${organizationId}/identity/link`,
  });
}

/** The caller's own link status, plus the discovery nudge when they are not linked. */
export async function getSlackIdentity(organizationId: number): Promise<AxiosResponse<SlackIdentityStatus>> {
  return httpClient({
    method: 'GET',
    url: `${SLACK_BASE_ROUTE}/${organizationId}/identity`,
  });
}

/** Unlinks the caller's own Slack account. Never anyone else's. */
export async function unlinkSlackIdentity(organizationId: number): Promise<AxiosResponse<void>> {
  return httpClient({
    method: 'DELETE',
    url: `${SLACK_BASE_ROUTE}/${organizationId}/identity`,
  });
}

/**
 * The caller's own delivery switch. MEMBER-guarded: the acting user comes from the
 * session, never from this request, so nobody can mute anybody else. Deliberately
 * separate from a system-imposed pause (`deliveryDisabledReason`), which this route
 * cannot clear.
 */
export const setSlackDeliveryEnabled = async (organizationId: number, enabled: boolean): Promise<void> => {
  await httpClient({
    method: 'PATCH',
    url: `${SLACK_BASE_ROUTE}/${organizationId}/identity`,
    data: { enabled },
  });
};
